import React, { useMemo } from 'react';
import {
  EmployeeObjectiveCategoryAssignmentFragment,
  ObjectiveCategoryFragment,
  ObjectiveMode,
  ObjectiveResultUpdateMode,
  ObjectiveWeightMode,
  ObjectiveTimePeriodFragment,
  useEmployeeAssignedCategoriesQuery,
  useGoalsContextPeriodsQuery,
} from '../../../../__generated__/graphql';
import { useFlairLocalStorage } from '../../../../hooks/useFlairLocalStorage';
import { useUserInfo } from '../../../../context/UserInfo';
import ServerError from '../../../../../../components/ServerError';
import { resolveObjectiveResultUpdateMode } from '../../hooks/useCanEditGoalResult';

export type GoalsContextType = {
  isManagerView: boolean;
  allActivePeriods: readonly ObjectiveTimePeriodFragment[];
  currentPeriod: ObjectiveTimePeriodFragment | null;
  currentPeriodId: string | null;
  setCurrentPeriodId: (periodId: string | null) => void;
  loading: boolean;
  developmentFrameworkEnabled: boolean;
  currentPeriodGoalCategories: readonly ObjectiveCategoryFragment[];
  employeeAssignedCategories: readonly EmployeeObjectiveCategoryAssignmentFragment[];
  objectiveMode: ObjectiveMode;
  objectiveResultUpdateMode: ObjectiveResultUpdateMode;
  objectiveWeightMode: ObjectiveWeightMode;
  hasAchievementScales: boolean;
};

const GoalsContext = React.createContext<GoalsContextType>({
  isManagerView: false,
  allActivePeriods: [],
  currentPeriod: null,
  currentPeriodId: null,
  setCurrentPeriodId: () => {},
  loading: true,
  developmentFrameworkEnabled: false,
  currentPeriodGoalCategories: [],
  employeeAssignedCategories: [],
  objectiveMode: ObjectiveMode.Okr,
  objectiveResultUpdateMode: ObjectiveResultUpdateMode.HrManager,
  objectiveWeightMode: ObjectiveWeightMode.None,
  hasAchievementScales: false,
});

type Props = {
  isManagerView: boolean;
  objectiveTimePeriodId?: string | null;
};

export const GoalsContextProvider: React.FC<Props> = (props) => {
  const [currentPeriodIdFromStorage, setCurrentPeriodIdFromStorage] =
    useFlairLocalStorage('goals_current_period_id', null);
  const { data, loading, error } = useGoalsContextPeriodsQuery();
  const { id: employeeId } = useUserInfo();

  const allActivePeriods = useMemo(
    () => data?.activeObjectivePeriods ?? [],
    [data?.activeObjectivePeriods],
  );

  const effectivePeriodId =
    props.objectiveTimePeriodId ?? currentPeriodIdFromStorage;

  const currentPeriod = useMemo(() => {
    if (!effectivePeriodId) {
      return null;
    }
    return (
      allActivePeriods.find((period) => period.Id === effectivePeriodId) ?? null
    );
  }, [effectivePeriodId, allActivePeriods]);

  const currentPeriodId = useMemo(() => {
    return currentPeriod?.Id ?? null;
  }, [currentPeriod]);

  const frameworkId = currentPeriod?.framework?.Id ?? null;

  const developmentFrameworkEnabled = useMemo(
    () => allActivePeriods.length > 0,
    [allActivePeriods.length],
  );

  const objectiveMode: ObjectiveMode = useMemo(
    () =>
      currentPeriod?.framework?.flair__Objective_Mode__c ?? ObjectiveMode.Okr,
    [currentPeriod?.framework?.flair__Objective_Mode__c],
  );

  const objectiveResultUpdateMode: ObjectiveResultUpdateMode = useMemo(
    () =>
      resolveObjectiveResultUpdateMode(
        currentPeriod?.framework?.flair__Objective_Result_Update_Mode__c,
      ),
    [currentPeriod?.framework?.flair__Objective_Result_Update_Mode__c],
  );

  const objectiveWeightMode: ObjectiveWeightMode = useMemo(
    () =>
      currentPeriod?.framework?.flair__Objective_Weight_Mode__c ??
      ObjectiveWeightMode.None,
    [currentPeriod?.framework?.flair__Objective_Weight_Mode__c],
  );

  const currentPeriodGoalCategories = useMemo(
    () => currentPeriod?.framework?.categories ?? [],
    [currentPeriod?.framework?.categories],
  );

  const { data: assignedCategoriesData } = useEmployeeAssignedCategoriesQuery({
    variables: {
      frameworkId: frameworkId!,
      employeeId,
      periodId: currentPeriodId!,
    },
    skip: !frameworkId || !currentPeriodId || !employeeId,
  });

  const employeeAssignedCategories = useMemo(
    () =>
      assignedCategoriesData?.developmentFramework
        ?.employeeAssignedCategories ?? [],
    [assignedCategoriesData?.developmentFramework?.employeeAssignedCategories],
  );

  const hasAchievementScales = useMemo(
    () =>
      employeeAssignedCategories.some(
        (assignment) => assignment.category.achievementScales.length > 0,
      ),
    [employeeAssignedCategories],
  );

  // we don't need to useEffect for this (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes)
  if (
    currentPeriodId === null &&
    !props.objectiveTimePeriodId &&
    allActivePeriods.length >= 1
  ) {
    setCurrentPeriodIdFromStorage(allActivePeriods[0].Id);
  }
  if (error) {
    return <ServerError />;
  }

  return (
    <GoalsContext.Provider
      value={{
        isManagerView: props.isManagerView,
        allActivePeriods,
        currentPeriod,
        currentPeriodId,
        setCurrentPeriodId: setCurrentPeriodIdFromStorage,
        loading,
        developmentFrameworkEnabled,
        currentPeriodGoalCategories,
        employeeAssignedCategories,
        objectiveMode,
        objectiveResultUpdateMode,
        objectiveWeightMode,
        hasAchievementScales,
      }}
      {...props}
    />
  );
};

export const useGoalsContext = (): GoalsContextType =>
  React.useContext(GoalsContext);
